import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import type { SessionListProps } from './types';
import './SessionList.css';

/**
 * SessionList Component
 * Displays list of chat sessions with ability to select, rename, and delete
 *
 * Optimizations:
 * - Wrapped with React.memo to prevent unnecessary re-renders
 * - useCallback for event handlers
 */
const SessionListComponent = ({
  sessions,
  currentSessionId,
  onSelectSession,
  onDeleteSession,
  onNewChat,
  onRenameSession
}: SessionListProps) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState<string>('');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Memoized handlers to prevent unnecessary re-renders
  const toggleMenu = useCallback((sessionId: string, e: React.MouseEvent): void => {
    e.stopPropagation();
    setOpenMenuId((prev) => prev === sessionId ? null : sessionId);
  }, []);

  const handleStartEdit = useCallback((sessionId: string, currentTitle: string): void => {
    setEditingId(sessionId);
    setEditTitle(currentTitle);
    setOpenMenuId(null);
  }, []);

  const handleSaveEdit = useCallback((sessionId: string): void => {
    if (editTitle.trim()) {
      onRenameSession(sessionId, editTitle.trim());
    }
    setEditingId(null);
    setEditTitle('');
  }, [editTitle, onRenameSession]);

  const handleCancelEdit = useCallback((): void => {
    setEditingId(null);
    setEditTitle('');
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>, sessionId: string): void => {
    if (e.key === 'Enter') {
      handleSaveEdit(sessionId);
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  }, [handleSaveEdit, handleCancelEdit]);

  return (
    <div className="session-list-container">
      <div className="session-list-header">
        <button onClick={onNewChat} className="new-chat-button" title="New Chat" type="button">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{marginRight: '8px'}}>
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          New Chat
        </button>
      </div>

      <div className="session-list">
        {sessions.length === 0 ? (
          <div className="empty-sessions">
            <p>No chat sessions yet.</p>
            <p>Start a new conversation!</p>
          </div>
        ) : (
          sessions.map((session) => (
            <div
              key={session.session_id}
              className={`session-item ${currentSessionId === session.session_id ? 'active' : ''}`}
            >
              <div
                className="session-content"
                onClick={() => onSelectSession(session.session_id)}
              >
                {editingId === session.session_id ? (
                  <div className="session-edit" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, session.session_id)}
                      autoFocus
                      className="session-title-input"
                    />
                    <div className="edit-buttons">
                      <button
                        onClick={() => handleSaveEdit(session.session_id)}
                        className="save-button"
                        title="Save"
                        type="button"
                      >
                        ✓
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="cancel-button"
                        title="Cancel"
                        type="button"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="session-info">
                      <div className="session-title">{session.title}</div>
                      <div className="session-meta">
                        <span className="session-date">{session.updated_date}</span>
                        <span
                          className="message-count-icon"
                          title={`${session.conversation_count} ${session.conversation_count === 1 ? 'message' : 'messages'}`}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </span>
                      </div>
                    </div>
                    <div className="session-actions" ref={openMenuId === session.session_id ? menuRef : null}>
                      <button
                        onClick={(e) => toggleMenu(session.session_id, e)}
                        className="kebab-button"
                        title="More options"
                        type="button"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <circle cx="12" cy="5" r="2" />
                          <circle cx="12" cy="12" r="2" />
                          <circle cx="12" cy="19" r="2" />
                        </svg>
                      </button>
                      {openMenuId === session.session_id && (
                        <div className="kebab-dropdown">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartEdit(session.session_id, session.title);
                            }}
                            className="dropdown-item"
                            title="Rename"
                            type="button"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuId(null);
                              if (window.confirm('Are you sure you want to delete this conversation?')) {
                                onDeleteSession(session.session_id);
                              }
                            }}
                            className="dropdown-item delete"
                            title="Delete"
                            type="button"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// PropTypes validation
SessionListComponent.propTypes = {
  sessions: PropTypes.arrayOf(
    PropTypes.shape({
      session_id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      message_count: PropTypes.number,
      conversation_count: PropTypes.number,
      url: PropTypes.string,
      created_at: PropTypes.string,
      updated_at: PropTypes.string,
      created_date: PropTypes.string,
      updated_date: PropTypes.string,
    })
  ).isRequired,
  currentSessionId: PropTypes.string,
  onSelectSession: PropTypes.func.isRequired,
  onDeleteSession: PropTypes.func.isRequired,
  onNewChat: PropTypes.func.isRequired,
  onRenameSession: PropTypes.func.isRequired,
};

// Wrap with memo for performance optimization
const SessionList = memo(SessionListComponent);

// Display name for debugging
SessionList.displayName = 'SessionList';

export default SessionList;
